/**
 * Block ids from census GEOIDs.
 *
 * The census-block map (blocks/index.html) links /blocks/<geoid>/, as its
 * block GeoJSON carries only the 15-digit GEOID. Block.id is the pipeline's
 * deterministic id, UUID5_SQL('Block', geoid20) in rundeck
 * pipelines/utilities/udf_uuid5.py: md5("<namespace prefix>|<geoid>") laid
 * out as 8-4-4-4-12. Block has no geoid index, so deriving the id here keeps
 * the detail lookup on block_id_unique instead of a multi-second scan.
 */

/** First 8 hex chars of uuid5(6ba7b810-9dad-11d1-80b4-00c04fd430c8, 'Block'). */
const BLOCK_NAMESPACE_PREFIX = 'e9403784';

export function isBlockGeoid(value: string): boolean {
  return /^\d{15}$/.test(value);
}

export function blockIdFromGeoid(geoid: string): string {
  const hex = md5Hex(`${BLOCK_NAMESPACE_PREFIX}|${geoid}`);
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

// RFC 1321 MD5 over UTF-8 bytes (WebCrypto has no MD5).
const SHIFTS = [7, 12, 17, 22, 5, 9, 14, 20, 4, 11, 16, 23, 6, 10, 15, 21];
const K = Array.from({ length: 64 }, (_, i) => Math.floor(Math.abs(Math.sin(i + 1)) * 2 ** 32) >>> 0);

export function md5Hex(input: string): string {
  const bytes = new TextEncoder().encode(input);
  const words = new Uint32Array((((bytes.length + 8) >>> 6) + 1) * 16);
  bytes.forEach((b, i) => { words[i >>> 2] |= b << ((i % 4) * 8); });
  words[bytes.length >>> 2] |= 0x80 << ((bytes.length % 4) * 8);
  words[words.length - 2] = (bytes.length * 8) >>> 0;
  words[words.length - 1] = Math.floor(bytes.length / 0x20000000);

  let a0 = 0x67452301, b0 = 0xefcdab89, c0 = 0x98badcfe, d0 = 0x10325476;
  for (let chunk = 0; chunk < words.length; chunk += 16) {
    let a = a0, b = b0, c = c0, d = d0;
    for (let i = 0; i < 64; i++) {
      let f: number, g: number;
      if (i < 16) { f = (b & c) | (~b & d); g = i; }
      else if (i < 32) { f = (d & b) | (~d & c); g = (5 * i + 1) % 16; }
      else if (i < 48) { f = b ^ c ^ d; g = (3 * i + 5) % 16; }
      else { f = c ^ (b | ~d); g = (7 * i) % 16; }
      const sum = (a + f + K[i] + words[chunk + g]) >>> 0;
      const s = SHIFTS[(i >>> 4) * 4 + (i % 4)];
      a = d; d = c; c = b;
      b = (b + ((sum << s) | (sum >>> (32 - s)))) >>> 0;
    }
    a0 = (a0 + a) >>> 0; b0 = (b0 + b) >>> 0; c0 = (c0 + c) >>> 0; d0 = (d0 + d) >>> 0;
  }
  return [a0, b0, c0, d0]
    .map(word => Array.from({ length: 4 }, (_, i) => ((word >>> (i * 8)) & 0xff).toString(16).padStart(2, '0')).join(''))
    .join('');
}
